import type { InterestTopic, PublicCatalogueItem } from '@ebu/contracts';

/**
 * Key art for programmes. A playable programme with a rights-cleared poster frame (Phase 5) shows
 * that picture; every other programme gets generated art drawn from metadata alone: an interest
 * colour field, a simple flat motif chosen from the programme id, the title, and a permanent
 * "fictional sample" mark. The same description is drawn as SVG in the page and onto a canvas
 * texture for the cinema posters in the 3D town, at 16:9.
 */

export type Motif = 'waves' | 'hills' | 'sun' | 'stars' | 'blocks' | 'leaves' | 'spark' | 'notes';

export interface PosterSpec {
  background: string;
  shape: string;
  ink: string;
  motif: Motif;
  title: string;
  lang: string;
  /** Same-origin poster image from the programme's media, when it is playable. */
  image: string | null;
}

const TOPIC_COLOURS: Record<InterestTopic, { background: string; shape: string; ink: string }> = {
  science: { background: '#2876a7', shape: '#8fc1e3', ink: '#ffffff' },
  nature: { background: '#287c51', shape: '#a9d8b8', ink: '#ffffff' },
  stories: { background: '#ea8a44', shape: '#fbd3b0', ink: '#1d3036' },
  making: { background: '#a46e46', shape: '#e6c4a6', ink: '#ffffff' },
  music: { background: '#126f68', shape: '#9fd6cf', ink: '#ffffff' },
  sport: { background: '#a95718', shape: '#f2c7a1', ink: '#ffffff' },
  history: { background: '#52636a', shape: '#c9d3d6', ink: '#ffffff' },
  animals: { background: '#5f7f2e', shape: '#d2e3ae', ink: '#ffffff' },
};

const MOTIFS: readonly Motif[] = [
  'waves',
  'hills',
  'sun',
  'stars',
  'blocks',
  'leaves',
  'spark',
  'notes',
];

/** Stable 32-bit FNV-1a hash, so the same programme always gets the same motif. */
export function hashId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export type PosterItem = Pick<PublicCatalogueItem, 'id' | 'title' | 'topics' | 'textLocale'> &
  Partial<Pick<PublicCatalogueItem, 'media'>>;

export function posterFor(item: PosterItem): PosterSpec {
  const colours = TOPIC_COLOURS[item.topics[0] ?? 'stories'];
  return {
    ...colours,
    motif: MOTIFS[hashId(item.id) % MOTIFS.length]!,
    title: item.title,
    lang: item.textLocale,
    image: item.media?.poster ?? null,
  };
}

/** SVG path data for each motif in a 320×180 box (shared by the SVG and canvas renderers). */
export const MOTIF_PATHS: Record<Motif, string[]> = {
  waves: [
    'M0 120 Q40 100 80 120 T160 120 T240 120 T320 120 V180 H0Z',
    'M0 145 Q40 125 80 145 T160 145 T240 145 T320 145 V180 H0Z',
  ],
  hills: [
    'M0 180 L0 120 Q80 60 160 120 Q240 70 320 110 L320 180Z',
    'M180 180 Q250 120 320 140 V180Z',
  ],
  sun: ['M250 60 m-34 0 a34 34 0 1 0 68 0 a34 34 0 1 0 -68 0', 'M0 150 H320 V180 H0Z'],
  stars: [
    'M60 40 l6 14 15 2 -11 10 3 15 -13 -7 -13 7 3 -15 -11 -10 15 -2z',
    'M250 70 l5 11 12 2 -9 8 2 12 -10 -6 -10 6 2 -12 -9 -8 12 -2z',
    'M170 30 l4 8 9 1 -7 6 2 9 -8 -4 -8 4 2 -9 -7 -6 9 -1z',
  ],
  blocks: ['M200 110 h50 v50 h-50z', 'M250 110 h50 v50 h-50z', 'M225 60 h50 v50 h-50z'],
  leaves: [
    'M250 160 Q230 80 290 40 Q310 110 250 160Z',
    'M200 170 Q170 110 220 80 Q245 130 200 170Z',
  ],
  spark: ['M240 20 L200 100 H235 L215 165 L290 75 H250 L275 20Z'],
  notes: [
    'M220 120 a14 10 0 1 0 28 0 a14 10 0 1 0 -28 0 M246 120 V50 L290 40 V110',
    'M276 110 a14 10 0 1 0 28 0 a14 10 0 1 0 -28 0',
  ],
};

/** Two short lines at most, so the title stays large. */
export function titleLines(title: string, maxChars = 18): string[] {
  const words = title.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxChars && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= 2) return lines;
  return [
    lines[0]!,
    `${lines
      .slice(1)
      .join(' ')
      .slice(0, maxChars - 1)}…`,
  ];
}

/**
 * Canvas version of the poster (the SVG version is PosterArt.tsx), for 3D textures. Pass the loaded
 * poster image to draw the real key art with the title on a dark band; without it the generated
 * art is drawn.
 */
export function drawPoster(
  ctx: CanvasRenderingContext2D,
  spec: PosterSpec,
  image?: CanvasImageSource,
): void {
  const { width, height } = ctx.canvas;
  ctx.save();
  ctx.scale(width / 320, height / 180);
  if (image) {
    ctx.drawImage(image, 0, 0, 320, 180);
    const lines = titleLines(spec.title);
    ctx.fillStyle = 'rgba(29, 48, 54, 0.82)';
    ctx.fillRect(0, 0, 320, 22 + lines.length * 30);
    ctx.fillStyle = '#ffffff';
  } else {
    ctx.fillStyle = spec.background;
    ctx.fillRect(0, 0, 320, 180);
    ctx.fillStyle = spec.shape;
    ctx.strokeStyle = spec.shape;
    ctx.lineWidth = 6;
    ctx.lineJoin = 'round';
    for (const d of MOTIF_PATHS[spec.motif]) {
      const path = new Path2D(d);
      ctx.fill(path);
      ctx.stroke(path);
    }
    ctx.fillStyle = spec.ink;
  }
  ctx.font = '700 26px "Atkinson Hyperlegible", system-ui, sans-serif';
  titleLines(spec.title).forEach((line, i) => ctx.fillText(line, 20, 42 + i * 30));
  ctx.restore();
}
